import React from 'react';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import { billingPiletModals, PaymentGatewayProps } from '@sharefiledev/billing-pilet-sdk';
import { Alert, Button, Col, Flex, Grid, Row, theme, Typography } from 'antd';
import { useApplicationContext } from '../../ApplicationContextProvider';
import { FeatureFlag } from '../../featureFlags';
import { analyticsIds, events, sendPendoTrackEvent } from '../../tracking/pendo';
import { t } from '../../translate';
import { getDefaultModalProps } from '../../utils';

const { Text } = Typography;

export interface GatewayProps extends PaymentGatewayProps {
	onCompletion?: (data: any) => Promise<void | boolean>;
}

export const CreditCardInfoPaymentGateway = (props: GatewayProps) => {
	const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

	const { piletApi } = useApplicationContext();
	const { token } = theme.useToken();
	const screens = Grid.useBreakpoint();
	const useMinimizedIFrame = getFeatureFlagValue<boolean>(
		FeatureFlag.EnablePaymentGatewayV2,
		false
	);

	return (
		<Flex
			vertical
			justify="flex-start"
			gap={token.sizeSM}
			style={{
				border: `1px solid ${token.colorBorder}`,
				borderRadius: token.borderRadius,
				padding: token.padding,
			}}
		>
			{props.successFlag && props.successFlag !== 'cancel' && (
				<Alert
					message={
						props.successFlag === 'success'
							? t('convert_to_paid.credit_card.confirmed_description')
							: t('convert_to_paid.credit_card.error_message')
					}
					type={props.successFlag === 'success' ? 'success' : 'error'}
					showIcon
					data-testid="credit-card-alert"
				/>
			)}
			{props.billingInfo?.CreditCardToken && (
				<Row data-testid="credit-card-billing-info">
					<Col span={8}>
						<Flex vertical gap={token.sizeXS}>
							<Text strong>{t('edit_billing_information.name_on_card')}</Text>
							<Text>
								{props.billingInfo?.CreditCardFirstName}{' '}
								{props.billingInfo?.CreditCardLastName}
							</Text>
						</Flex>
					</Col>
					<Col span={8}>
						<Flex vertical gap={token.sizeXS}>
							<Text strong>{t('edit_billing_information.credit_card.card_number')}</Text>
							<Text>********{props.billingInfo?.CreditCardToken}</Text>
						</Flex>
					</Col>
					<Col span={8}>
						<Flex vertical gap={token.sizeXS}>
							<Text strong>
								{t('edit_billing_information.credit_card.card_exp_date')}
							</Text>
							<Text>
								{props.billingInfo?.CreditCardExpirationMonth}/
								{props.billingInfo?.CreditCardExpirationYear}
							</Text>
						</Flex>
					</Col>
				</Row>
			)}
			<Row>
				<Col>
					<Button
						type="primary"
						onClick={async () => {
							setIsProcessing(true);
							piletApi.showModal<PaymentGatewayProps & { orderCode?: string }>(
								billingPiletModals.creditCardModal,
								{
									...props,
									onCompleteInteraction: async data => {
										if (props.onCompletion) {
											await props.onCompletion(data);
										}
									},
									onCancelInteraction: async () => {
										sendPendoTrackEvent(events.closeEditCCModal);
									},
									modalProps: getDefaultModalProps(screens, useMinimizedIFrame),
								}
							);
							setIsProcessing(false);
						}}
						disabled={props.isEditCreditCardInfoDisabled}
						data-testid="credit-card-info-button"
						loading={isProcessing}
						data-analytics-id={analyticsIds.editCCButton}
					>
						{props.billingInfo?.CreditCardToken
							? t('convert_to_paid.credit_card.edit')
							: t('convert_to_paid.credit_card.add')}
					</Button>
				</Col>
			</Row>
		</Flex>
	);
};
